export const movementDeltaLabels = {
  onHand: 'On Hand',
  available: 'Available',
  reserved: 'Reserved',
  quarantine: 'Quarantine',
  damaged: 'Damaged',
  committed: 'Committed',
  pending: 'Pending',
  display: 'Display',
  monogram: 'Monogram',
}

export const getMovementQuantity = movementCount => Number(
  movementCount?.count?.on_hand ??
  movementCount?.count?.quantity ??
  movementCount?.quantity ??
  movementCount?.count ??
  0
)

// A movement that records where stock went while the balance change sits on
// another row, e.g. a replenishment's "Replenish to X" row at the 3PL source,
// whose own stock sync moves the balance. It is never shown as a stock change.
export const isInformationalMovement = movement => movement?.informational === true

// The SKUs and unsigned quantities an informational movement records.
export const getInformationalMovementItems = counts => (counts || [])
  .map(movementCount => ({
    sku: movementCount?.variant?.sku,
    count: Math.abs(getMovementQuantity(movementCount)),
  }))
  .filter(item => item.sku && item.count !== 0)

export const getMovementDeltas = movementCount => {
  if (isInformationalMovement(movementCount)) return []

  const rawDeltas = movementCount?.count?.deltas ?? movementCount?.deltas ?? {}
  const deltas = Object.entries(rawDeltas)
    .map(([field, quantity]) => ({
      field,
      label: movementDeltaLabels[field] || field,
      quantity: Number(quantity),
    }))
    .filter(delta => Number.isFinite(delta.quantity) && delta.quantity !== 0)

  if (deltas.length > 0) return deltas

  const quantity = getMovementQuantity(movementCount)
  return quantity === 0
    ? []
    : [{ field: 'onHand', label: movementDeltaLabels.onHand, quantity }]
}

export const getMovementDisplayDeltas = movementCount => {
  const deltas = getMovementDeltas(movementCount)
  const onHandDelta = deltas.find(delta => delta.field === 'onHand')

  return deltas.filter(delta => (
    delta.field !== 'available'
    || !onHandDelta
    || delta.quantity !== onHandDelta.quantity
  ))
}

// Stock In/Out remains an on-hand summary when onHand changed. For auxiliary-only
// movements, expose the actual buckets so audit rows never render as blank.
export const getMovementSummaryDeltas = movementCount => {
  const deltas = getMovementDeltas(movementCount)
  const onHandDelta = deltas.find(delta => delta.field === 'onHand')
  return onHandDelta ? [onHandDelta] : deltas
}

const mergeMovementCounts = (existingCounts, incomingCounts) => {
  const countsBySku = new Map()
  const allCounts = [...existingCounts, ...incomingCounts]

  allCounts.forEach(item => {
    const sku = item?.variant?.sku
    if (!sku) return

    const existing = countsBySku.get(sku)
    if (!existing) {
      countsBySku.set(sku, {
        ...item,
        count: {
          ...item.count,
          deltas: { ...(item.count?.deltas || {}) },
        },
      })
      return
    }

    const deltas = { ...(existing.count?.deltas || {}) }
    Object.entries(item.count?.deltas || {}).forEach(([field, quantity]) => {
      deltas[field] = Number(deltas[field] || 0) + Number(quantity || 0)
    })
    existing.count = {
      ...existing.count,
      on_hand: Number(existing.count?.on_hand || 0)
        + Number(item.count?.on_hand || 0),
      deltas,
    }
  })

  return Array.from(countsBySku.values())
}

const PICK_GROUP_PREFIX = 'legacy:pick:'

const getMovementGroupId = row => {
  if (row?.movementGroupId) return String(row.movementGroupId)

  const locationCode = row?.location?.code || ''
  if (
    row?.movementType === 'CYCLE_COUNT'
    && row?.message === 'Approved audit'
  ) {
    const createdAt = new Date(row.createdAt)
    if (!Number.isNaN(createdAt.getTime())) {
      createdAt.setMilliseconds(0)
      return `legacy:audit:${locationCode}:${createdAt.toISOString()}`
    }
  }

  const referenceId = row?.referenceId || ''
  // Picks carry no movement group: one pick or send writes its order's picks at the
  // location in the same minute (the CSV export folds them the same way).
  if (
    referenceId
    && row?.movementType === 'SHIPMENT'
    && String(row?.message || '').startsWith('Picked Stock for fulfillment')
  ) {
    const createdAt = new Date(row.createdAt)
    if (!Number.isNaN(createdAt.getTime())) {
      return `${PICK_GROUP_PREFIX}${locationCode}:${referenceId}:${createdAt.toISOString().slice(0, 16)}`
    }
  }

  if (
    referenceId
    && ['TRANSFER', 'RETURN', 'CYCLE_COUNT'].includes(row?.movementType)
  ) {
    return `legacy:${row.movementType}:${locationCode}:${referenceId}`
  }

  return undefined
}

export const groupMovementHistoryRows = rows => {
  const groupedRows = []
  const groupedIndexes = new Map()

  rows.forEach(row => {
    const movementGroupId = getMovementGroupId(row)
    if (!movementGroupId) {
      groupedRows.push(row)
      return
    }

    const groupKey = JSON.stringify([
      movementGroupId,
      row.location?.code || '',
    ])
    const existingIndex = groupedIndexes.get(groupKey)

    if (existingIndex === undefined) {
      groupedIndexes.set(groupKey, groupedRows.length)
      groupedRows.push({
        ...row,
        movementGroupId,
        counts: mergeMovementCounts([], row.counts || []),
      })
      return
    }

    const existing = groupedRows[existingIndex]
    existing.counts = mergeMovementCounts(
      existing.counts || [],
      row.counts || [],
    )
  })

  return groupedRows
}
